/**
 * Band resolution.
 *
 * Thresholds are read from a calibration config (fit against golden
 * profiles) rather than hard-coded at render sites. Callers can pass their
 * own thresholds if they've loaded a per-framework calibration row from
 * `confidence_calibration`.
 */

import type { ConfidenceBand } from './types';

export interface BandThresholds {
  /** value >= high → 'high' */
  high: number;
  /** value >= medium → 'medium' */
  medium: number;
  /** value >= low → 'low', otherwise 'insufficient' */
  low: number;
}

/**
 * Default thresholds. Chosen so that:
 *  - a fresh single-source profile lands in `low`,
 *  - 2+ agreeing sources with recent data lands in `medium`,
 *  - the ensemble consensus + coverage need to be strong to reach `high`.
 * These are the *fallback* — a calibrated row overrides them.
 */
export const DEFAULT_BAND_THRESHOLDS: BandThresholds = {
  high: 0.72,
  medium: 0.5,
  low: 0.25,
};

export function resolveBand(
  value: number | null,
  thresholds: BandThresholds = DEFAULT_BAND_THRESHOLDS,
): ConfidenceBand {
  if (value === null || value < thresholds.low) return 'insufficient';
  if (value >= thresholds.high) return 'high';
  if (value >= thresholds.medium) return 'medium';
  return 'low';
}
